import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Scale, FileText, Building2, Mail, Server, User } from "lucide-react";

// Mentions Légales + CGU de base — Nold Go (Belgique).
// Rédigées selon les exigences du Code de droit économique belge (art. XII.6),
// du RGPD (règlement UE 2016/679) et de la directive e-commerce 2000/31/CE.
// À faire relire par un juriste avant mise en production commerciale.
export default function MentionsLegales() {
  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="mentions-legales">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" data-testid="mentions-back"
          className="inline-flex items-center gap-1 h-10 px-4 rounded-full bg-white border border-[#E2DFD8] text-sm font-semibold text-[#1A1A1A] hover:border-[#059669]">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>

        <header className="mt-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#059669]/10 text-[#059669] px-3 py-1 text-[10px] font-black uppercase tracking-widest">
            <Scale className="w-3 h-3" /> Informations légales
          </div>
          <h1 className="mt-3 font-head font-black text-4xl sm:text-5xl text-[#1A1A1A]">Mentions légales</h1>
          <p className="mt-2 text-sm text-[#75807A]">Dernière mise à jour : 3 septembre 2026</p>
        </header>

        {/* Section 1 — Éditeur */}
        <section className="mt-8 rounded-3xl bg-white border border-[#E2DFD8] p-6" data-testid="mentions-editor">
          <h2 className="flex items-center gap-2 font-head font-black text-2xl text-[#1A1A1A]">
            <Building2 className="w-6 h-6 text-[#059669]" /> 1. Éditeur du site
          </h2>
          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-y-3 text-sm">
            <dt className="text-[#75807A] font-semibold">Responsable</dt>
            <dd className="sm:col-span-2 text-[#1A1A1A] font-medium"><User className="w-4 h-4 inline mr-1 text-[#059669]" /> Bertrand Ngnogue</dd>

            <dt className="text-[#75807A] font-semibold">Adresse</dt>
            <dd className="sm:col-span-2 text-[#1A1A1A] font-medium">Rue Jourdan 71 A0, 1060 Saint-Gilles, Bruxelles, Belgique</dd>

            <dt className="text-[#75807A] font-semibold">Numéro d'entreprise</dt>
            <dd className="sm:col-span-2 text-[#1A1A1A] font-medium font-mono">BCE 1040580267</dd>

            <dt className="text-[#75807A] font-semibold">Contact</dt>
            <dd className="sm:col-span-2 text-[#1A1A1A] font-medium">
              <a href="mailto:contact@noldgo.com" className="text-[#059669] hover:underline" data-testid="mentions-email">
                <Mail className="w-4 h-4 inline mr-1" /> contact@noldgo.com
              </a>
            </dd>

            <dt className="text-[#75807A] font-semibold">Site web</dt>
            <dd className="sm:col-span-2 text-[#1A1A1A] font-medium">
              <a href="https://www.noldgo.com" className="text-[#059669] hover:underline">https://www.noldgo.com</a>
            </dd>
          </dl>
        </section>

        {/* Section 2 — Hébergeur */}
        <section className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-6" data-testid="mentions-host">
          <h2 className="flex items-center gap-2 font-head font-black text-2xl text-[#1A1A1A]">
            <Server className="w-6 h-6 text-[#059669]" /> 2. Hébergement
          </h2>
          <p className="mt-3 text-sm text-[#3B423E]">
            Le site <strong>noldgo.com</strong> est hébergé par la plateforme <strong>Emergent</strong> (Emergent Labs).
          </p>
          <p className="mt-2 text-sm text-[#3B423E]">
            Site : <a href="https://emergentagent.com" className="text-[#059669] hover:underline" target="_blank" rel="noopener noreferrer">emergentagent.com</a>
          </p>
        </section>

        {/* Section 3 — Propriété intellectuelle */}
        <section className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-6">
          <h2 className="flex items-center gap-2 font-head font-black text-2xl text-[#1A1A1A]">
            <FileText className="w-6 h-6 text-[#059669]" /> 3. Propriété intellectuelle
          </h2>
          <p className="mt-3 text-sm text-[#3B423E] leading-relaxed">
            L'ensemble des éléments du site Nold Go (logo, textes, graphismes, images, vidéos, code source) est protégé par le droit d'auteur (loi du 30 juin 1994, Code de droit économique livre XI). Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans l'accord écrit préalable de l'éditeur, est interdite et pourra donner lieu à des poursuites.
          </p>
        </section>

        {/* Section 4 — CGU */}
        <section className="mt-4 rounded-3xl bg-white border border-[#E2DFD8] p-6" data-testid="mentions-cgu">
          <h2 className="flex items-center gap-2 font-head font-black text-2xl text-[#1A1A1A]">
            <Scale className="w-6 h-6 text-[#059669]" /> 4. Conditions Générales d'Utilisation
          </h2>

          <h3 className="mt-4 font-head font-black text-lg text-[#1A1A1A]">4.1 Objet</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Nold Go est une plateforme numérique de mise en relation. Elle propose deux services distincts sur le territoire belge :
          </p>
          <ul className="mt-2 ml-5 list-disc space-y-1 text-sm text-[#3B423E]">
            <li><strong>Nold Go Food</strong> — livraison de repas entre restaurants partenaires et clients, réalisée par des livreurs indépendants (vélo/e-bike).</li>
            <li><strong>Nold Go VTC</strong> — mise en relation entre passagers et chauffeurs indépendants exploitant un service de transport rémunéré de personnes.</li>
          </ul>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.2 Rôle de la plateforme</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Nold Go n'est <strong>ni transporteur, ni restaurateur</strong>. La plateforme se limite à mettre en relation le client avec un partenaire indépendant (restaurateur, livreur ou chauffeur VTC), qui exécute la prestation sous sa propre responsabilité, sa propre couverture d'assurance et son propre agrément.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.3 Compte utilisateur</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            L'inscription est gratuite. L'utilisateur s'engage à fournir des informations exactes et à ne pas partager son compte. Toute utilisation frauduleuse (fausse identité, moyen de paiement volé, revente du service) entraîne la suspension immédiate sans remboursement.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.4 Commandes et paiement</h3>
          <ul className="mt-2 ml-5 list-disc space-y-1 text-sm text-[#3B423E]">
            <li>Les paiements sont traités par <strong>Stripe Payments Europe</strong> (aucun numéro de carte n'est stocké par Nold Go).</li>
            <li>Le client est débité au moment de la validation. Un refus du restaurant entraîne un remboursement automatique sous 5-10 jours ouvrables.</li>
            <li>Les frais de livraison sont calculés dynamiquement selon la distance (2,50 € forfaitaires + 0,50 €/km au-delà d'un km).</li>
            <li>Les prix affichés incluent la TVA belge en vigueur (12 % restauration à emporter ; 21 % pour les prestations VTC).</li>
          </ul>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.5 Droit de rétractation</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Conformément à l'article VI.53 du Code de droit économique, le droit de rétractation de 14 jours <strong>ne s'applique pas</strong> aux commandes de biens périssables (repas préparés) ni aux prestations de transport de personnes exécutées immédiatement.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.6 Annulation par le client</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Une commande peut être annulée <strong>sans frais tant que le restaurant ne l'a pas acceptée</strong>. Une fois la préparation lancée, aucun remboursement n'est possible sauf non-livraison caractérisée ou avarie constatée.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.7 Livraison sécurisée (haute valeur)</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Pour toute commande d'un montant supérieur ou égal à 80 €, un <strong>code de signature à 4 chiffres</strong> est communiqué au client. Il doit être remis au livreur pour attester de la bonne remise. À défaut, le livreur peut prendre une photo horodatée. Cette double sécurité protège les deux parties.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.8 Responsabilité du restaurateur partenaire</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Le restaurateur partenaire s'engage à respecter la réglementation belge en matière d'hygiène alimentaire (arrêté royal du 14 novembre 2003 relatif à l'autocontrôle, notification obligatoire à l'AFSCA) et à fournir un service conforme à la commande. Nold Go se réserve le droit de suspendre tout partenaire dont le taux de refus, la qualité ou les délais dérogent aux standards de service.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.9 Responsabilité du chauffeur / livreur</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Les chauffeurs VTC et livreurs sont des <strong>travailleurs indépendants</strong>. Ils doivent disposer d'une carte professionnelle valide (arrêté royal du 15 novembre 2004 pour les VTC en Région bruxelloise), d'une assurance civile professionnelle et d'un véhicule en règle. Nold Go vérifie ces documents au moment de l'onboarding mais décline toute responsabilité pénale ou civile lors de l'exécution de la course.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.10 Comportement des utilisateurs</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Toute forme de harcèlement, propos discriminatoire, dégradation matérielle ou refus de paiement entraîne le bannissement définitif de la plateforme et pourra faire l'objet d'un signalement aux autorités compétentes.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.11 Protection des données (RGPD)</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Les données personnelles sont traitées conformément au Règlement UE 2016/679 (RGPD) et à la loi belge du 30 juillet 2018. Le détail des traitements, durées de conservation et droits (accès, rectification, suppression, portabilité, opposition) figurent dans notre <Link to="/confidentialite" className="text-[#059669] hover:underline">Politique de confidentialité</Link>. Pour exercer vos droits : <a href="mailto:contact@noldgo.com" className="text-[#059669] hover:underline">contact@noldgo.com</a>.
          </p>

          <h3 className="mt-5 font-head font-black text-lg text-[#1A1A1A]">4.12 Litiges et loi applicable</h3>
          <p className="mt-2 text-sm text-[#3B423E] leading-relaxed">
            Les présentes conditions sont régies par le droit belge. En cas de litige, les parties s'efforcent de trouver un accord amiable. À défaut, le litige sera porté devant les <strong>juridictions de Bruxelles</strong>. Le consommateur peut également recourir à la plateforme européenne de règlement en ligne : <a href="https://ec.europa.eu/consumers/odr" className="text-[#059669] hover:underline" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a>.
          </p>
        </section>

        {/* Section 5 — Assurance RGPD express */}
        <section className="mt-4 rounded-3xl bg-[#F0FDF4] border-2 border-[#059669]/30 p-6">
          <h2 className="flex items-center gap-2 font-head font-black text-2xl text-[#1A1A1A]">
            <ShieldCheck className="w-6 h-6 text-[#059669]" /> 5. Contact & réclamations
          </h2>
          <p className="mt-3 text-sm text-[#3B423E]">
            Pour toute question, réclamation ou demande liée à vos données personnelles, écrivez à :<br/>
            <a href="mailto:contact@noldgo.com" className="text-[#059669] font-bold hover:underline">contact@noldgo.com</a>
          </p>
        </section>

        <footer className="mt-8 text-center text-xs text-[#75807A]">
          © 2026 Nold Go · Bertrand Ngnogue · BCE 1040580267 · Bruxelles, Belgique
        </footer>
      </div>
    </div>
  );
}
